import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchTechnicianPanelTrackableBatch } from '../../technician-panel/services/technicianPanelApi';

const POLL_MS = 90_000;

function idsFingerprint(list) {
  if (!Array.isArray(list) || !list.length) return '';
  return [...new Set(list.map((id) => String(id ?? '').trim()).filter(Boolean))].sort().join('|');
}

function setIdsIfChanged(setTrackableIds, next) {
  const list = Array.isArray(next) ? next : [];
  setTrackableIds((prev) => (idsFingerprint(prev) === idsFingerprint(list) ? prev : list));
}

/** App Go Premium (id_produto = 4 via go_id_produto da sessão). */
function isGoPremium(goIdProduto) {
  return Number(goIdProduto) === 4;
}

/**
 * Batch leve de técnicos com GPS fresco (< 30 min) para ícone no Board.
 * Só ativo com licença Go Premium. Impacto baixo: 1 POST a cada ~90s.
 *
 * @param {string[]} technicianIds codtec / ids visíveis no board
 * @param {number|string|null} [goIdProduto] — go_id_produto da sessão (3=Basic, 4=Premium)
 * @returns {Set<string>} ids rastreáveis (normalizados e raw quando possível)
 */
export function useBoardTrackableTechnicians(technicianIds, goIdProduto = null) {
  const [trackableIds, setTrackableIds] = useState([]);
  const premium = isGoPremium(goIdProduto);

  const idsKey = useMemo(() => {
    const list = Array.isArray(technicianIds)
      ? [...new Set(technicianIds.map((id) => String(id ?? '').trim()).filter(Boolean))].sort()
      : [];
    return list.join('|');
  }, [technicianIds]);
  const idsRef = useRef([]);
  idsRef.current = Array.isArray(technicianIds)
    ? [...new Set(technicianIds.map((id) => String(id ?? '').trim()).filter(Boolean))]
    : [];

  useEffect(() => {
    let cancelled = false;

    if (!premium) {
      setIdsIfChanged(setTrackableIds, []);
      return undefined;
    }

    const ids = idsRef.current;
    if (!ids.length) {
      setIdsIfChanged(setTrackableIds, []);
      return undefined;
    }

    const load = async () => {
      try {
        const data = await fetchTechnicianPanelTrackableBatch(ids);
        if (cancelled) return;
        setIdsIfChanged(setTrackableIds, Array.isArray(data.trackableIds) ? data.trackableIds : []);
      } catch {
        if (!cancelled) setIdsIfChanged(setTrackableIds, []);
      }
    };

    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [idsKey, premium]);

  return useMemo(() => {
    const set = new Set();
    if (!premium) return set;
    trackableIds.forEach((id) => {
      const raw = String(id ?? '').trim();
      if (!raw) return;
      set.add(raw);
      const digits = raw.replace(/\D/g, '');
      if (digits) {
        set.add(digits);
        set.add(digits.padStart(4, '0').slice(-4));
      }
    });
    return set;
  }, [trackableIds, premium]);
}
